import { makeStyles, MenuItem, Theme } from "@material-ui/core";
import { FilterList } from "@material-ui/icons";
import React, { useMemo } from "react";

import {
  FilterMenu,
  FilterSearch,
} from "@ofqual/portal-components/components/ResponseFilter";

import { useLookup, useSearch, useUrl } from "@ofqual/portal-core/core/hooks";
import { SearchField } from "@ofqual/portal-components/components/SearchField";
import { useSelector } from "react-redux";
import { authSelector } from "../../redux/selectors";
import { UserType } from "../../redux/types";
import { QueryParams } from "@ofqual/portal-core/core/types";
import {
  aoService,
  aoServiceSearch,
  cachedLevelsService,
  sectorSubjectService,
} from "../../services";
import { Select } from "@ofqual/portal-components/components/Select";
import { toGroupedList } from "@ofqual/portal-core/core/utils/listHelpers";
import { ItemType } from "@ofqual/portal-components/components/types";
import { ensureParamIsArray } from "@ofqual/portal-core/core/utils/urlHelper";

const useStyles = makeStyles((theme: Theme) => {
  return {
    root: {
      "& fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
    menuItem: {
      "&:hover": {
        backgroundColor: "transparent",
      },
    },
  };
});

export const FilterControls: React.FC = () => {
  const classes = useStyles();

  const {
    searchParams,
    navigateToNextSearch,
    searchParamsFiltersOnly,
  } = useSearch(useUrl());

  const { token, userType } = useSelector(authSelector);

  const isOfqualUser = userType === UserType.Ofqual;

  const mappedOrgLookupService = useMemo(
    () => ({
      get: (id: string) => aoService.get(id.toString(), { token }),
      search: (params?: QueryParams) =>
        aoServiceSearch.search(params, { token }),
    }),
    [token]
  );

  const [sectorSubjectAreaLookups] = useLookup(sectorSubjectService);

  const groupedSectorSubjectAreas = toGroupedList(sectorSubjectAreaLookups, {
    id: "id",
    label: "label",
    group: "group",
  });

  const [levelsLookups] = useLookup(
  {
    service: cachedLevelsService,
    id: "levelCode",
    label: "levelDescription",
  });
  

  return (
    <>
      <FilterSearch
        title="Search..."
        value={searchParams.search}
        onChange={(value) =>
          navigateToNextSearch({
            search: value,
          })
        }
        dataTest={"search"}
      />
      &nbsp;&nbsp;
      <FilterMenu
        Icon={FilterList}
        title="Filters"
        dataTest="filter"
        search={{ searchParamsFiltersOnly, navigateToNextSearch }}
      >
        {(workingParams, dirtyParams, addParam) => (
          <form>
            {isOfqualUser ? (
              <MenuItem className={classes.menuItem}>
                <SearchField
                  searchFieldVariant="multiple"
                  className={
                    dirtyParams.includes("awardingOrganisation")
                      ? classes.root
                      : undefined
                  }
                  onChange={(e: any) =>
                    addParam("awardingOrganisation", e.target.value)
                  }
                  value={workingParams.awardingOrganisation}
                  label="Awarding Organisations"
                  datatest="filter-aos"
                  service={mappedOrgLookupService}
                />
              </MenuItem>
            ) : null}
            <MenuItem className={classes.menuItem}>
              <Select
                selectVariant="multiple"
                className={
                  dirtyParams.includes("unitSectorSubjectArea")
                    ? classes.root
                    : undefined
                }
                label="Unit sector subject area"
                data-test="unit-ssa"
                value={ensureParamIsArray(workingParams.unitSectorSubjectArea)}
                onChange={(e) =>
                  addParam("unitSectorSubjectArea", e.target.value)
                }
              >
                {
                  groupedSectorSubjectAreas.map((x) => ({
                    ...x,
                    id: x.id?.toString(),
                  })) as ItemType[]
                }
              </Select>
            </MenuItem>
            <MenuItem className={classes.menuItem}>
              <Select
                selectVariant="multiple"
                className={
                  dirtyParams.includes("unitLevel")
                    ? classes.root
                    : undefined
                }
                label="Unit level"
                data-test="unit-level"
                value={ensureParamIsArray(workingParams.unitLevel)}
                onChange={(e) =>
                  addParam("unitLevel", e.target.value)
                }
              >
                  {levelsLookups.filter(
                    (value, index, self) =>
                      self.findIndex((x) => x.id === value.id) === index
                  )}
              </Select>
            </MenuItem>            
          </form>
        )}
      </FilterMenu>
    </>
  );
};
